import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DatePipe, UpperCasePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { Client } from 'app/core/models/client.model';
import { ClientChore, ClientChoreChecklistStatus } from 'app/core/models/client-chore.model';
import { ClientFileQualityChecklist } from 'app/core/models/client-file-quality-checklist.model';
import { ClientChoreName } from 'app/core/enums/client-chore-name.enum';
import { ClientChoresService, PagedClientChores } from 'app/core/services/client-chores.service';
import { ClientsService } from 'app/core/services/clients.service';
import { DownloadService } from 'app/core/services/download.service';
import { BlankValueDashPipe } from '../../../shared/pipes/blank-value-dash.pipe';
import { ClientChoreStatusComponent } from '../../components/client-chore-status/client-chore-status.component';
import { ClientFileQualityChecklistComponent } from '../../components/client-file-quality-checklist/client-file-quality-checklist.component';

const ehrInstanceValueBlankPlaceholder = "(blank)";
const initialChoreCount = 5;

@Component({
    templateUrl: './capture-admin-client-ehr-file-review-checklist.component.html',
    styleUrls: ['./capture-admin-client-ehr-file-review-checklist.component.scss'],
    imports: [DatePipe,
              UpperCasePipe,
              RouterLink,
              BlankValueDashPipe,
              ClientChoreStatusComponent,
              ClientFileQualityChecklistComponent,
              MatIcon,
              MatProgressSpinner]
})
export class CaptureAdminClientEhrFileReviewChecklistComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private clientsService = inject(ClientsService);
  private clientChoresService = inject(ClientChoresService);
  private downloadService = inject(DownloadService);
  private clientId: number = null;
  private clientRouted = false;
  private selectedChoreId: number = null;

  client: Client = null;
  clientChores: ClientChore[] = [];
  // A chore linked to directly can be older than the loaded ones, in which case it is shown on its
  // own below them.
  extraChore: ClientChore = null;
  choreCount = 0;
  showingAllChores = false;
  selectedChore: ClientChore = null;
  checklist: ClientFileQualityChecklist = null;
  loading = true;
  loadingAllChores = false;
  loadingChecklist = false;
  savingApproval = false;
  savingChecklistStatus = false;

  // The page is reachable by chore, which selects that chore, or by client, which selects the chore
  // named by the `chore` query param, if any. The two are separate routes, so the client route keeps
  // its selection in a query param: routing to the chore route instead would rebuild the whole page.
  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const clientId = Number(params.get('clientId'));

      if (clientId) {
        this.loadForClient(clientId);
      } else {
        this.loadForChore(Number(params.get('id')));
      }
    });

    this.route.queryParamMap.subscribe(params => {
      if (!this.clientRouted) return;

      this.selectedChoreId = Number(params.get('chore')) || null;

      // A load in flight applies the selection itself once it has a list to select from.
      if (!this.loading) this.applySelection();
    });
  }

  // Selecting a chore stays on the route the page arrived by: the client route carries the selection
  // in a query param, and the chore route in its path.
  choreLink(clientChore: ClientChore): (string | number)[] {
    if (this.clientRouted) return [];

    return ['/capture-admin/tools/client-ehr-file-reviews', clientChore.id, 'checklist'];
  }

  choreLinkQueryParams(clientChore: ClientChore): { chore: number } {
    return this.clientRouted ? { chore: clientChore.id } : null;
  }

  // The chores come back most recent first, so the loaded ones are the newest. The extra chore is
  // older than all of them and so belongs at the end.
  get visibleClientChores(): ClientChore[] {
    return this.extraChore ? [...this.clientChores, this.extraChore] : this.clientChores;
  }

  get hasMoreChores(): boolean {
    return !this.showingAllChores && this.choreCount > this.clientChores.length;
  }

  get emrSystemNames(): string {
    return this.client?.emrSystems?.map(emrSystem => emrSystem.name).join(', ');
  }

  get allowedEhrInstanceValues(): string {
    return this.client?.effectiveAllowedClientFileEhrInstanceValues
      ?.map(value => value || ehrInstanceValueBlankPlaceholder)
      ?.join(', ');
  }

  get hrsaIds(): string {
    return this.client?.effectiveHrsaIdStrings?.join(', ');
  }

  get approvedForProcessing(): boolean {
    return !!this.client?.approvedForProcessingAt;
  }

  // The selected chore is the same object the list renders, so recording the verdict on it also
  // updates the badge in its row.
  setChecklistStatus(status: ClientChoreChecklistStatus) {
    this.savingChecklistStatus = true;

    this.clientChoresService.setChecklistStatus(this.selectedChore.id, status).subscribe({
      next: clientChore => {
        this.selectedChore.checklistStatus = clientChore.checklistStatus;
        this.selectedChore.checklistStatusByDisplay = clientChore.checklistStatusByDisplay;
        this.savingChecklistStatus = false;
      },
      // Leave the buttons usable so a failed update can be retried.
      error: () => this.savingChecklistStatus = false
    });
  }

  // The rest of the client's chores are only fetched when they are asked for. The selected chore is
  // in the new list too, so the selection moves to that copy to keep its row highlighted.
  showAllChores() {
    this.loadingAllChores = true;

    this.fetchClientChores('all').subscribe({
      next: (result: PagedClientChores) => {
        this.setClientChores(result);
        this.showingAllChores = true;
        this.loadingAllChores = false;

        if (this.selectedChore) {
          this.selectedChore = this.findLoadedChore(this.selectedChore.id) || this.selectedChore;
        }
      },
      // Leave the button usable so a failed load can be retried.
      error: () => this.loadingAllChores = false
    });
  }

  downloadReport() {
    this.downloadService.getDownloadUrl(this.selectedChore.reportExpirableDownloadPath)
      .subscribe(expirableDownload => this.downloadService.download(expirableDownload.url));
  }

  // Approving is a toggle: an approved client's button clears the approval.
  toggleApprovedForProcessing() {
    const approved = this.approvedForProcessing;

    this.savingApproval = true;

    const request = approved
      ? this.clientsService.unapproveForProcessing(this.client.id)
      : this.clientsService.approveForProcessing(this.client.id);

    request.subscribe({
      next: client => {
        this.client = client;
        this.savingApproval = false;
      },
      // Leave the button usable so a failed approval can be retried.
      error: () => this.savingApproval = false
    });
  }

  private loadForClient(clientId: number) {
    this.loading = true;
    this.clientId = clientId;
    this.clientRouted = true;
    this.loadClient(clientId);
    this.loadClientChores();
  }

  // A chore already loaded belongs to the client whose header and list are on screen, so it only
  // moves the selection; anything else is a fresh arrival, and fetching the chore is also what tells
  // us which client to load.
  private loadForChore(clientChoreId: number) {
    this.selectedChoreId = clientChoreId;

    if (this.findLoadedChore(clientChoreId)) {
      this.applySelection();
      return;
    }

    this.loading = true;

    this.clientChoresService.get(clientChoreId).subscribe(clientChore => {
      this.clientId = clientChore.clientId;
      this.loadClient(clientChore.clientId);
      this.loadClientChores(clientChore);
    });
  }

  private loadClient(clientId: number) {
    this.clientsService.get(clientId).subscribe(client => {
      this.client = client;
    });
  }

  // Only the most recent chores are loaded, so a chore the URL names can be missing from the list. A
  // chore already fetched to find its client is reused rather than fetched a second time.
  private loadClientChores(fetchedChore: ClientChore = null) {
    this.fetchClientChores(initialChoreCount).subscribe((result: PagedClientChores) => {
      this.setClientChores(result);
      this.showingAllChores = false;
      this.loading = false;
      this.applySelection(fetchedChore);
    });
  }

  // A selected chore too old to be among the loaded ones is fetched on its own and shown below them.
  private applySelection(fetchedChore: ClientChore = null) {
    if (!this.selectedChoreId) {
      this.clearSelection();
      return;
    }

    const loadedChore = this.findLoadedChore(this.selectedChoreId);

    if (loadedChore) {
      this.selectChore(loadedChore);
      return;
    }

    if (fetchedChore?.id === this.selectedChoreId) {
      this.extraChore = fetchedChore;
      this.selectChore(fetchedChore);
      return;
    }

    this.clientChoresService.get(this.selectedChoreId).subscribe(clientChore => {
      // Ignore a response for a chore the user has already navigated away from.
      if (this.selectedChoreId !== clientChore.id) return;

      this.extraChore = clientChore;
      this.selectChore(clientChore);
    });
  }

  private fetchClientChores(pageSize: number | 'all') {
    return this.clientChoresService.getList(1,
                                            pageSize,
                                            [],
                                            [String(this.clientId)],
                                            [],
                                            [ClientChoreName.clientFileQualityCheck]);
  }

  private setClientChores(result: PagedClientChores) {
    this.clientChores = result.clientChores;
    this.choreCount = result.meta.paging.count;
    this.extraChore = null;
  }

  private findLoadedChore(clientChoreId: number): ClientChore {
    return this.visibleClientChores.find(clientChore => clientChore.id === clientChoreId);
  }

  // Navigating back to the client page without a chore leaves the list on screen with nothing picked.
  private clearSelection() {
    this.selectedChore = null;
    this.checklist = null;
    this.loadingChecklist = false;
  }

  private selectChore(clientChore: ClientChore) {
    if (!clientChore || clientChore === this.selectedChore) return;

    this.selectedChore = clientChore;
    this.checklist = null;
    this.loadingChecklist = true;

    this.clientChoresService.getChecklist(clientChore.id).subscribe({
      next: checklist => {
        // Ignore a response for a chore the user has already navigated away from.
        if (this.selectedChore?.id !== checklist.id) return;

        this.checklist = checklist;
        this.loadingChecklist = false;
      },
      error: () => this.loadingChecklist = false
    });
  }
}
